import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { Filter, Search } from 'lucide-react';
import { useDeferredValue, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Input, Select } from '@/ui/primitives';
import { cn } from '@/lib/utils';
import { useMoverCard } from '../api';
import { useDragPan } from '@/lib/use-drag-pan';
import type { CardFieldMap, Dominio, KanbanCard, KanbanResponse } from '../types';
import { KanbanColumn } from './KanbanColumn';

interface Props {
  data: KanbanResponse;
  fieldMap: CardFieldMap;
  dominio: Dominio;
  pipe?: string;
  onCardSelect: (cardId: string) => void;
  /** Estilo "coluna do kanban": container surface2 sem borda + input sem contorno (usado no Ambiental). */
  bareSearch?: boolean;
}

function normalizeText(value: unknown) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function compactToken(value: unknown) {
  return normalizeText(value).replace(/[^a-z0-9]/g, '');
}

function digitsOnly(value: unknown) {
  return String(value ?? '').replace(/\D/g, '');
}

function buildSearchValues(card: KanbanCard, fieldMap: CardFieldMap) {
  const fields = fieldMap(card);
  return [
    card.id,
    fields.title,
    fields.subtitle,
    fields.meta,
    ...(fields.chips ?? []).map((chip) => chip.value),
    card.cliente_nome,
    card.cliente_cpf,
    card.imovel_denominacao,
    card.imovel_matricula,
    card.municipio,
    card.numero_protocolo,
    card.numero_processo,
    card.codigo_car,
    card.car_codigo,
    card.titular,
    card.titular_cnpj_cpf,
    card.nome_marca,
  ].filter(Boolean);
}

function explicitCardId(query: string) {
  const match = query.trim().match(/^(?:card\s*)?#\s*(\d+)$/i);
  return match?.[1] ?? null;
}

export function matchesSearch(card: KanbanCard, fieldMap: CardFieldMap, query: string) {
  const trimmed = query.trim();
  if (!trimmed) return true;

  const cardId = explicitCardId(trimmed);
  if (cardId) return String(card.id) === cardId;

  const values = buildSearchValues(card, fieldMap);
  const textHaystack = normalizeText(values.join(' | '));
  const compactHaystack = compactToken(values.join(''));
  const digitsHaystack = digitsOnly(values.join(''));

  const normalizedQuery = normalizeText(trimmed);
  const compactQuery = compactToken(trimmed);
  const digitsQuery = digitsOnly(trimmed);

  if (normalizedQuery && textHaystack.includes(normalizedQuery)) return true;
  if (compactQuery.length >= 3 && compactHaystack.includes(compactQuery)) return true;
  if (digitsQuery.length >= 3 && digitsHaystack.includes(digitsQuery)) return true;
  return false;
}

function humanize(s: string) {
  const t = s.replace(/_/g, ' ').trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

// Eixos de filtro por domínio; as opções são derivadas dos cards do pipe aberto,
// então cada pipe mostra só os campos/valores que existem no seu kanban.
const FILTER_FIELDS: Record<string, { key: string; label: string }[]> = {
  credito: [
    { key: 'instituicao_nome', label: 'Instituição' },
    { key: 'responsavel_nome', label: 'Assistente da operação' },
    { key: 'status', label: 'Status' },
    { key: 'finalidade_codigo', label: 'Finalidade' },
    { key: 'programa_nome', label: 'Programa' },
  ],
  // Ambiental: candidatos por pipe (CAR/triagem/licenciamento têm dimensões
  // distintas). Só entram no popover os que tiverem 2+ valores no board atual.
  ambiental: [
    { key: 'status', label: 'Status' },
    { key: 'orgao_competente', label: 'Órgão' },
    { key: 'servico_tipo', label: 'Serviço' },
    { key: 'status_negociacao', label: 'Negociação' },
    { key: 'status_final_analise_car', label: 'Resultado da análise' },
    { key: 'licenciamento_etapa', label: 'Etapa' },
    { key: 'estudo_tecnico_tipo', label: 'Tipo de estudo' },
    { key: 'origem_card', label: 'Origem' },
  ],
  fundiario: [
    { key: 'status', label: 'Status' },
    { key: 'cartorio', label: 'Cartório' },
    { key: 'municipio', label: 'Município' },
    { key: 'responsavel', label: 'Responsável técnico' },
  ],
  juridico: [{ key: 'status', label: 'Status' }],
};

export function KanbanBoard({ data, fieldMap, dominio, pipe, onCardSelect, bareSearch = false }: Props) {
  const mover = useMoverCard(dominio, pipe);
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const searchId = useId();
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filterWrapRef = useRef<HTMLDivElement | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );
  const pan = useDragPan<HTMLDivElement>();

  const filterFields = FILTER_FIELDS[dominio] ?? [{ key: 'status', label: 'Status' }];
  // Opções por campo, derivadas dos cards do board atual. Campo com menos de 2
  // valores distintos não vira filtro — filtrar por ele não excluiria nada.
  // É isso que adapta o popover a cada pipe sem hardcode por pipe.
  const fieldOptions = useMemo(
    () =>
      filterFields
        .map((f) => {
          const set = new Set<string>();
          data.colunas.forEach((col) =>
            col.processos.forEach((c) => {
              const v = (c as Record<string, unknown>)[f.key];
              if (v != null && String(v).trim()) set.add(String(v));
            }),
          );
          return { ...f, options: [...set].sort() };
        })
        .filter((f) => f.options.length > 1),
    [data, filterFields],
  );
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  function setFilter(key: string, value: string) {
    setFilters((prev) => {
      const next = { ...prev };
      if (value) next[key] = value;
      else delete next[key];
      return next;
    });
  }

  // Reseta filtros ao trocar de pipe (valores podem não existir no novo).
  useEffect(() => { setFilters({}); setFiltersOpen(false); }, [pipe]);
  // Fecha popover ao clicar fora / ESC.
  useEffect(() => {
    if (!filtersOpen) return undefined;
    function onDown(e: MouseEvent) {
      if (filterWrapRef.current && !filterWrapRef.current.contains(e.target as Node)) setFiltersOpen(false);
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setFiltersOpen(false); }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [filtersOpen]);

  const filteredColumns = data.colunas.map((col) => ({
    ...col,
    processos: col.processos.filter((card) => {
      if (!matchesSearch(card, fieldMap, deferredSearch)) return false;
      for (const [key, val] of Object.entries(filters)) {
        if (val && String((card as Record<string, unknown>)[key] ?? '') !== val) return false;
      }
      return true;
    }),
  }));
  const totalCards = data.colunas.reduce((acc, col) => acc + col.processos.length, 0);
  const visibleCards = filteredColumns.reduce((acc, col) => acc + col.processos.length, 0);
  const hasSearch = deferredSearch.trim().length > 0;

  function handleDragEnd(e: DragEndEvent) {
    const cardId = String(e.active.id);
    const targetColuna = e.over?.id;
    if (!targetColuna) return;

    // Detecta coluna atual do card pra evitar mutation se soltou na mesma coluna.
    const fromColuna = data.colunas.find((c) =>
      c.processos.some((p) => p.id === cardId),
    );
    if (!fromColuna || String(fromColuna.id) === String(targetColuna)) return;

    // Regra do FNO: ao arrastar pra "Perdido", motivo é obrigatório.
    const targetCol = data.colunas.find((c) => String(c.id) === String(targetColuna));
    const extra: Record<string, unknown> = {};
    if (dominio === 'credito' && targetCol?.nome === 'Perdido') {
      const card = fromColuna.processos.find((p) => p.id === cardId) as
        | Record<string, unknown>
        | undefined;
      const motivoAtual = (card?.motivo_perda_cancelamento as string | null | undefined) ?? '';
      const motivo = window.prompt(
        'Informe o motivo da perda/cancelamento (obrigatório para mover para "Perdido"):',
        motivoAtual,
      );
      if (motivo == null) return; // cancelou — abortar drop
      const trimmed = motivo.trim();
      if (!trimmed) {
        window.alert('O motivo é obrigatório para mover para "Perdido".');
        return;
      }
      extra.motivo_perda_cancelamento = trimmed;
    }

    mover.mutate({ id: cardId, coluna: String(targetColuna), extra });
  }

  return (
    <div className="flex flex-col gap-5">
      {bareSearch ? (
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="relative w-full max-w-[460px]">
            <Search
              size={16}
              strokeWidth={1.9}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted"
            />
            <input
              id={searchId}
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por cliente, CPF, card, imóvel, protocolo ou serviço"
              className="h-10 w-full rounded-[10px] bg-surface2 pl-10 pr-3 text-sm text-text outline-none transition-shadow placeholder:text-text-muted focus:ring-2 focus:ring-[var(--accent-soft)]"
            />
          </div>
          <div ref={filterWrapRef} className={cn('relative', fieldOptions.length === 0 && 'hidden')}>
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              className={cn(
                'inline-flex h-10 items-center gap-2 rounded-[10px] px-4 text-sm transition-colors',
                activeFilterCount > 0 || filtersOpen
                  ? 'bg-[var(--filter-active-bg)] text-[var(--filter-active-fg)]'
                  : 'bg-surface2 text-text-2 hover:text-text',
              )}
            >
              <Filter size={15} strokeWidth={1.9} />
              Filtrar
              {activeFilterCount > 0 && (
                <span className="inline-flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-[var(--filter-badge-bg)] px-1 text-[0.62rem] font-bold leading-none text-[var(--filter-badge-fg)]">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {filtersOpen && (
              <div className="absolute right-0 top-[calc(100%+6px)] z-50 flex max-h-[70vh] w-[268px] flex-col gap-3.5 overflow-y-auto rounded-[14px] border border-border2 bg-surface p-4 shadow-[0_14px_36px_rgba(0,0,0,0.22)]">
                <p className="text-[0.8rem] font-semibold text-text">Filtrar</p>
                {fieldOptions.length === 0 ? (
                  <p className="text-xs text-text-muted">Sem filtros disponíveis.</p>
                ) : (
                  fieldOptions.map((f) => (
                    <div key={f.key} className="flex flex-col gap-1.5">
                      <p className="text-[0.7rem] font-medium uppercase tracking-wider text-text-2">{f.label}</p>
                      <Select
                        value={filters[f.key] ?? ''}
                        onChange={(e) => setFilter(f.key, e.target.value)}
                        className="border-transparent bg-surface2 text-[0.82rem]"
                      >
                        <option value="">Todos</option>
                        {f.options.map((opt) => (
                          <option key={opt} value={opt}>
                            {humanize(opt)}
                          </option>
                        ))}
                      </Select>
                    </div>
                  ))
                )}
                {activeFilterCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setFilters({})}
                    className="text-left text-[0.75rem] text-text-2 hover:text-accent"
                  >
                    Limpar filtros
                  </button>
                )}
              </div>
            )}
          </div>
          {(hasSearch || activeFilterCount > 0) && (
            <span className="w-full text-xs text-text-muted">
              {visibleCards} de {totalCards} card{totalCards === 1 ? '' : 's'}
            </span>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-border/70 bg-surface/70 px-4 py-3">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col gap-1">
              <label
                htmlFor={searchId}
                className="text-xs font-semibold uppercase tracking-[0.16em] text-text-muted"
              >
                Buscar card
              </label>
              <p className="text-xs text-text-muted">
                Pesquise por card, titular, CPF, matr&iacute;cula, protocolo ou trecho do n&uacute;mero do CAR.
              </p>
            </div>
            <div className="relative w-full md:max-w-md">
              <Search
                size={15}
                strokeWidth={1.8}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
              />
              <Input
                id={searchId}
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Ex.: Fernando, 12520176776, 2D90"
                className="pl-9"
              />
            </div>
          </div>
          {hasSearch && (
            <p className="mt-2 text-xs text-text-muted">
              {visibleCards} de {totalCards} card{totalCards === 1 ? '' : 's'} encontrado
              {visibleCards === 1 ? '' : 's'} para &ldquo;{deferredSearch.trim()}&rdquo;.
            </p>
          )}
        </div>
      )}

      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        {/* -mx-8/px-8: o scroll sangra até a borda da página sem cortar a sombra
            dos cards. Sem padding-top: o respiro acima vem do gap do wrapper,
            senão o vão até a toolbar fica maior que o das demais seções. */}
        <div
          ref={pan.ref}
          onMouseDown={pan.onMouseDown}
          className={`overflow-x-auto -mx-8 px-8 pb-6 select-none ${pan.cursorClass}`}
        >
          <div className="flex gap-3 items-start">
            {filteredColumns.map((col, index) => (
              <KanbanColumn
                key={col.id}
                column={col}
                fieldMap={fieldMap}
                onCardSelect={onCardSelect}
                featured={index === 0}
                totalCount={data.colunas[index]?.processos.length}
                cardsMuted={
                  dominio === 'credito'
                  && ['perdido', 'arquivado', 'indeferido'].includes(
                    col.nome
                      .normalize('NFD')
                      .replace(/[\u0300-\u036f]/g, '')
                      .toLowerCase()
                      .trim(),
                  )
                }
              />
            ))}
          </div>
        </div>
      </DndContext>
    </div>
  );
}
